import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, ProgressBar } from 'react-bootstrap';
import { useQueries } from '@tanstack/react-query';
import { fetchJob } from '../../features/jobs/api/jobsApi';

import AdvanceTable from '../base/AdvanceTable';
import AdvanceTableFooter from '../base/AdvanceTableFooter';
import Badge from '../base/Badge';
import useAdvanceTable from '../../hooks/useAdvanceTable';
import AdvanceTableProvider from '../../providers/AdvanceTableProvider';
import { JobEstimateProvider, JobEstimate } from '../../features/machine-intervals/EstimateProvider.js';
import SearchBox from '../common/SearchBox';
import FilterTab from '../common/FilterTab';
import { formatCoreSpec } from '../../helpers/productLabel';

// canonical phase names -> labels (English, like the rest of the app)
const PHASE_LABEL = {
  unwinding: 'Unwinding',
  pre_stranding: 'Pre-stranding',
  conductor_isolation: 'Isolation',
  rope: 'Rope',
  interior_sheat: 'Interior Sheath',
  exterior_sheat: 'Exterior Sheath',
  armouring: 'Armouring',
  coiling: 'Coiling',
  rewinding: 'Rewinding'
};
const PHASE_SHORT = {
  unwinding: 'Unwind',
  pre_stranding: 'Pre-str',
  conductor_isolation: 'Isol',
  rope: 'Rope',
  interior_sheat: 'Inner',
  exterior_sheat: 'Outer',
  armouring: 'Armour',
  coiling: 'Coil',
  rewinding: 'Rewind'
};
const phaseLabel = n => PHASE_LABEL[n] || n;
const phaseShort = n => PHASE_SHORT[n] || phaseLabel(n);

const fmt = n => Number(n || 0).toLocaleString();

const statusBg = s => {
  const u = String(s || '').toUpperCase();
  if (u === 'ACTIVE' || u === 'RUNNING') return 'success';
  if (u === 'PENDING') return 'warning';
  if (u === 'COMPLETED' || u === 'DONE') return 'info';
  return 'secondary';
};

const phaseOrderOf = job => {
  const fg = Array.isArray(job?.phasesForGant) ? job.phasesForGant : [];
  return fg.length ? fg : Object.keys(job?.phases || {});
};

const PhaseStepper = ({ order = [], phases = {}, currentIdx = -1 }) => (
  <div className="d-flex flex-wrap align-items-center gap-1">
    {order.map((name, i) => {
      const prog = Number(phases?.[name]?.progress || 0);
      const done = prog >= 100;
      const current = i === currentIdx;

      let cls = 'bg-light text-muted border';
      if (done) cls = 'bg-success-subtle text-success border border-success';
      else if (current)
        cls = 'bg-info-subtle text-info border border-info fw-bold';

      const suffix = done ? ' ✓' : prog > 0 ? ` ${prog}%` : '';

      return (
        <React.Fragment key={name}>
          {i > 0 && <span className="text-300">›</span>}
          <span className={`px-2 py-1 rounded ${cls}`} style={{ fontSize: 10 }}>
            {phaseShort(name)}
            {suffix}
          </span>
        </React.Fragment>
      );
    })}
  </div>
);

const columns = [
  {
    accessorKey: 'productLabel',
    header: 'Product',
    cell: ({ row: { original } }) => (
      <div className="d-flex flex-column">
        <span className="fw-semibold fs-9">{original.productLabel}</span>
        <span className="text-700 fs-10">
          {formatCoreSpec(original)} mm²
        </span>
      </div>
    ),
    meta: {
      cellProps: { className: 'py-2' },
      headerProps: { style: { minWidth: 160 } }
    }
  },
  {
    accessorKey: 'jobCode',
    header: 'Job',
    cell: ({ row: { original } }) =>
      original.jobId ? (
        <Link
          to={`/apps/project-management/project-details/${original.jobId}`}
          className="text-decoration-none fs-10 fw-semibold"
        >
          {original.jobCode || String(original.jobId).slice(-8)}
        </Link>
      ) : (
        <span className="text-700 fs-10">—</span>
      ),
    meta: { headerProps: { style: { width: '12%' } } }
  },
  {
    accessorKey: 'meters',
    header: 'Allocated',
    cell: ({ row: { original } }) => (
      <span className="fw-semibold">{fmt(original.meters)} m</span>
    ),
    meta: {
      cellProps: { className: 'text-end' },
      headerProps: { style: { width: '9%' }, className: 'text-end' }
    }
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row: { original } }) => (
      <Badge bg={statusBg(original.status)} variant="phoenix" className="fs-10">
        {String(original.status || 'PENDING').toUpperCase()}
      </Badge>
    ),
    meta: { headerProps: { style: { width: '9%' } } }
  },
  {
    accessorKey: 'currentPhase',
    header: 'Current Phase',
    cell: ({ row: { original } }) =>
      original.currentPhase ? (
        <Badge bg="primary" variant="phoenix" className="fs-10">
          {phaseLabel(original.currentPhase)}
        </Badge>
      ) : original.status ? (
        <Badge bg="success" variant="phoenix" className="fs-10">
          Done
        </Badge>
      ) : (
        <span className="text-700 fs-10">—</span>
      ),
    meta: { headerProps: { style: { width: '12%' } } }
  },
  {
    accessorKey: 'phaseOrder',
    header: 'Phases',
    cell: ({ row: { original } }) => (
      <PhaseStepper
        order={original.phaseOrder}
        phases={original.phasesObj}
        currentIdx={original.currentIdx}
      />
    ),
    enableSorting: false,
    meta: {
      cellProps: { className: 'py-2' },
      headerProps: { style: { minWidth: 230 } }
    }
  },
  {
    id: 'estimate',
    header: 'Est. Left',
    enableSorting: false,
    cell: ({ row: { original } }) => <JobEstimate jobId={original.jobId} />,
    meta: {
      cellProps: { className: 'py-2' },
      headerProps: { style: { minWidth: 110 } }
    }
  },
  {
    accessorKey: 'progress',
    header: 'Progress',
    cell: ({ row: { original } }) => {
      const pct = Number(original.progress || 0);
      return (
        <div className="d-flex align-items-center gap-2">
          <ProgressBar
            now={pct}
            style={{ height: 5, flex: 1 }}
            variant={pct >= 100 ? 'success' : pct > 0 ? 'primary' : 'secondary'}
          />
          <span className="fs-10 text-700" style={{ minWidth: 28 }}>
            {pct}%
          </span>
        </div>
      );
    },
    meta: { headerProps: { style: { width: '12%' } } }
  }
];

const OrderTraceability = ({ order }) => {
  const lines = Array.isArray(order?.lines) ? order.lines : [];

  // one entry per (line, allocated job)
  const pairs = useMemo(() => {
    const out = [];
    for (const line of lines) {
      const p = line?.product || {};
      const productLabel = `${String(p.cableName || '').toUpperCase()}${
        p.color ? ` (${p.color})` : ''
      }`;
      for (const a of line?.allocation?.allocations || []) {
        out.push({
          lineId: line.lineId,
          productLabel,
          numberOfConductors: p.numberOfConductors,
          section: p.section,
          jobId: a.jobId,
          jobCode: a.jobCode,
          meters: a.meters
        });
      }
    }
    return out;
  }, [lines]);

  const jobQueries = useQueries({
    queries: pairs.map(p => ({
      queryKey: ['jobs', 'detail', p.jobId],
      queryFn: () => fetchJob(p.jobId),
      enabled: !!p.jobId,
      refetchInterval: 8000
    }))
  });

  // recompute rows when any job's data refreshes
  const dataKey = jobQueries.map(q => q.dataUpdatedAt || 0).join(',');
  const allRows = useMemo(() => {
    return pairs.map((p, i) => {
      const job = jobQueries[i]?.data;
      const order2 = phaseOrderOf(job);
      const curIdx = job
        ? order2.findIndex(n => Number(job?.phases?.[n]?.progress || 0) < 100)
        : -1;
      return {
        ...p,
        status: job?.status || (jobQueries[i]?.isLoading ? 'LOADING' : ''),
        progress: Number(job?.progress || 0),
        phaseOrder: order2,
        phasesObj: job?.phases || {},
        currentIdx: curIdx,
        currentPhase: curIdx === -1 ? null : order2[curIdx]
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pairs, dataKey]);

  const [statusFilter, setStatusFilter] = useState('all');
  const [searchText, setSearchText] = useState('');

  const tabItems = useMemo(() => {
    const c = { all: allRows.length, ACTIVE: 0, PENDING: 0, COMPLETED: 0 };
    for (const r of allRows) {
      const s = String(r.status || '').toUpperCase();
      if (s === 'ACTIVE' || s === 'RUNNING') c.ACTIVE++;
      else if (s === 'PENDING') c.PENDING++;
      else if (s === 'COMPLETED' || s === 'DONE') c.COMPLETED++;
    }
    return [
      { label: 'All', value: 'all', count: c.all },
      { label: 'Active', value: 'ACTIVE', count: c.ACTIVE },
      { label: 'Pending', value: 'PENDING', count: c.PENDING },
      { label: 'Completed', value: 'COMPLETED', count: c.COMPLETED }
    ];
  }, [allRows]);

  const filteredRows = useMemo(() => {
    let rows = allRows;
    if (statusFilter === 'ACTIVE') {
      rows = rows.filter(r => {
        const s = String(r.status || '').toUpperCase();
        return s === 'ACTIVE' || s === 'RUNNING';
      });
    } else if (statusFilter === 'PENDING') {
      rows = rows.filter(r => String(r.status || '').toUpperCase() === 'PENDING');
    } else if (statusFilter === 'COMPLETED') {
      rows = rows.filter(r => {
        const s = String(r.status || '').toUpperCase();
        return s === 'COMPLETED' || s === 'DONE';
      });
    }
    if (searchText.trim()) {
      const q = searchText.toLowerCase();
      rows = rows.filter(
        r =>
          (r.productLabel || '').toLowerCase().includes(q) ||
          (r.jobCode || '').toLowerCase().includes(q) ||
          (r.currentPhase || '').toLowerCase().includes(q) ||
          String(r.numberOfConductors || '').includes(q) ||
          String(r.section || '').includes(q)
      );
    }
    return rows;
  }, [allRows, statusFilter, searchText]);

  const table = useAdvanceTable({
    data: filteredRows,
    columns,
    pageSize: 10,
    pagination: true,
    sortable: true
  });

  return (
    <Card>
      <Card.Body>
        <div className="d-flex align-items-center justify-content-between mb-3">
          <Card.Title as="h3" className="mb-0">
            Production Traceability
          </Card.Title>
          <span className="text-muted fs-10">
            manufacturing phase per allocated product
          </span>
        </div>

        {!pairs.length ? (
          <p className="text-muted mb-0">
            No products launched into production yet for this order.
          </p>
        ) : (
          <JobEstimateProvider jobIds={filteredRows.map(r => r.jobId)}>
          <AdvanceTableProvider {...table}>
            <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between mb-2">
              <FilterTab
                tabItems={tabItems}
                activeKey={statusFilter}
                onSelect={setStatusFilter}
              />
              <SearchBox
                placeholder="Search product, job, phase..."
                onChange={e => setSearchText(e.target.value || '')}
              />
            </div>

            {filteredRows.length === 0 ? (
              <div className="text-700 py-3 text-center fs-9">
                No matching production rows
              </div>
            ) : (
              <>
                <AdvanceTable
                  tableProps={{
                    className: 'phoenix-table border-top border-200 fs-9'
                  }}
                />
                <AdvanceTableFooter pagination />
              </>
            )}
          </AdvanceTableProvider>
          </JobEstimateProvider>
        )}
      </Card.Body>
    </Card>
  );
};

export default OrderTraceability;
